<template>
	<div v-if="isMobile" class="vcx-nav-logo-mobile vcx-flex vcx-justify-between" ref="mobileTopNav">
		<BaseLink :link="vcxLandingLink">
			<BaseSvgIcon name="fundrise-vcx-logo-stacked" width="87px" height="40px" title="VCX by Fundrise" />
		</BaseLink>
		<BaseButton
			classes="vcx-button-primary vcx-nav__learn-more-button vcx-px-100 vcx-py-25 vcx-body-sm"
			data-test="vcx-flyout-button"
			@click="showFlyOutMenu"
			>Learn more</BaseButton
		>
	</div>

	<nav
		class="vcx-nav"
		:class="{ 'vcx-nav--mobile': isMobile }"
		:style="mobileTopNavHeight > 0 ? { '--vcx-top-nav-height': mobileTopNavHeight + 'px' } : undefined"
		aria-label="Main navigation"
		data-context="vcx-nav"
	>
		<div class="vcx-nav__container">
			<BaseLink v-if="!isMobile" :link="vcxLandingLink" class="vcx-nav__logo">
				<BaseSvgIcon name="fundrise-vcx-white" width="167px" height="36px" title="VCX by Fundrise" />
			</BaseLink>
			<div class="vcx-nav__menu">
				<BaseLink
					v-for="link in vcxNavLinks"
					:id="`nav-${link.hash}`"
					:key="link.hash"
					:link="link"
					:classes="navLinkClasses(link.hash)"
					@click="handleNavClick($event, link.hash)"
					><span class="vcx-block">{{ link.text }}</span></BaseLink
				>
				<BaseButton
					v-if="!isMobile"
					classes="vcx-button-primary vcx-nav__learn-more-button vcx-px-100-smo vcx-py-25-smo vcx-body-sm"
					data-test="vcx-flyout-button"
					@click="showFlyOutMenu"
					>Learn more</BaseButton
				>
			</div>
		</div>
	</nav>
	<!-- Flyout Menu -->
	<VcxFlyOut v-model="showFlyOut" />
</template>

<script setup lang="ts">
import { useElementBounding, useEventListener, useIntersectionObserver, useThrottleFn } from '@vueuse/core';
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import VcxFlyOut from '@components/vcx/vcx-fly-out.vue';
import { vcxLandingLink, vcxNavLinks } from '@constants/vcx';
import { useAppStore } from '@stores/app';

const router = useRouter();
const route = useRoute();

const appStore = useAppStore();

const isMobile = computed(() => appStore.isMobile);

const showFlyOut = ref(false);

const mobileTopNavRef = useTemplateRef('mobileTopNav');
const { height: mobileTopNavHeight } = useElementBounding(mobileTopNavRef);
const activeHash = ref('');
const sectionElements = ref<Array<HTMLElement>>([]);

useIntersectionObserver(
	sectionElements,
	(entries) => {
		for (const entry of entries) {
			if (entry.isIntersecting) {
				activeHash.value = `#${entry.target.id}`;
			}

			if (!entry.isIntersecting && activeHash.value === `#${entry.target.id}`) {
				activeHash.value = '';
			}
		}
	},
	{ rootMargin: '-50px 0px -55%' }
);

function navLinkClasses(hash: string | undefined): string {
	return hash && hash === activeHash.value ? 'vcx-nav__link vcx-nav__link--active' : 'vcx-nav__link';
}

function handleNavClick(event: Event, hash: string | undefined): void {
	event.preventDefault();
	if (!hash) return;

	if (route.name !== 'vcx-landing') {
		router.push({ name: 'vcx-landing', hash });
	} else {
		router.replace({ hash });
	}
}

function showFlyOutMenu() {
	showFlyOut.value = true;
}

onMounted(async () => {
	if (route.query.invest === 'true') {
		showFlyOutMenu();
	}
	await nextTick();
	sectionElements.value = vcxNavLinks
		.map((link) => (link.hash ? document.getElementById(link.hash.replace('#', '')) : null))
		.filter((el): el is HTMLElement => el !== null);
});
</script>

<style scoped lang="scss">
@use '../../styles/vcx/responsive.scss' as *;

.vcx-nav-logo-mobile {
	padding: var(--vcx-space-100);
	position: sticky;
	top: 0;
	z-index: 101;
	backdrop-filter: blur(32px);
	transition:
		background-color var(--vcx-transition-base),
		box-shadow var(--vcx-transition-base);
}

.vcx-nav {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	z-index: 100;
	backdrop-filter: blur(32px);
	transition:
		background-color var(--vcx-transition-base),
		box-shadow var(--vcx-transition-base),
		transform var(--vcx-transition-fast);

	&--mobile {
		position: sticky;
		top: var(--vcx-top-nav-height, 88px);
		backdrop-filter: none;
		background-color: var(--vcx-bg-dark);
		border-color: var(--vcx-gray-70);
		border-width: 1px 0 1px 0;
		border-style: solid;

		&.vcx-nav--hidden {
			transform: translateY(-100%);
		}
	}

	&__container {
		display: flex;
		align-items: center;
		justify-content: space-between;
		max-width: var(--vcx-max-width);
		margin: 0 auto;
		padding: var(--vcx-space-150) var(--vcx-space-300);

		.vcx-nav--mobile & {
			padding: 0 var(--vcx-space-100);
		}
	}

	&__menu {
		display: flex;
		align-items: center;
		gap: var(--vcx-space-100);

		@include vcx-media-lg {
			gap: var(--vcx-space-200);
		}

		.vcx-nav--mobile & {
			width: 100%;
			gap: var(--vcx-space-50);
			justify-content: space-around;
		}
	}

	&__link {
		border-bottom: 2px solid transparent;
		font-size: var(--vcx-font-size-body-md);
		font-weight: var(--vcx-font-weight-regular);
		line-height: var(--vcx-line-height-body-md);
		color: var(--vcx-gray-white);
		padding: var(--vcx-space-50) var(--vcx-space-100);
		text-decoration: none;
		transition:
			opacity var(--vcx-transition-fast),
			color var(--vcx-transition-base),
			border-color var(--vcx-transition-base);

		span {
			transition: transform var(--vcx-transition-base);
		}

		&:hover {
			opacity: 0.8;

			span {
				transform: translateY(-2px);
			}
		}

		&--active {
			border-color: var(--vcx-gray-white);
		}

		@include vcx-media-lg {
			font-size: var(--vcx-font-size-body-lg);
			line-height: var(--vcx-line-height-body-lg);
		}

		.vcx-nav--mobile & {
			font-size: var(--vcx-font-size-body-md);
		}
	}

	&--scrolled {
		background-color: var(--vcx-bg-dark);
		box-shadow: var(--vcx-shadow-md);
	}
}

.vcx-nav__learn-more-button {
	height: auto;
	font-size: var(--vcx-font-size-body-sm);
	line-height: var(--vcx-line-height-body-sm);

	@include vcx-media-md {
		font-size: var(--vcx-font-size-body-md);
		line-height: var(--vcx-line-height-body-md);
	}

	@include vcx-media-lg {
		font-size: var(--vcx-font-size-body-lg);
		line-height: var(--vcx-line-height-body-lg);
	}

	&:focus,
	&:focus-within {
		background-color: var(--vcx-brand-primary);
		color: var(--vcx-content-primary);
	}

	&:hover:not(:disabled) {
		background-color: rgba(244, 99, 58, 0.75);
		color: var(--vcx-content-primary);
	}

	&:focus-visible {
		outline: 2px solid var(--vcx-brand-primary);
		outline-offset: 2px;
		background-color: var(--vcx-brand-primary);
		color: var(--vcx-content-primary);
	}

	.vcx-nav--mobile & {
		font-size: var(--vcx-font-size-body-lg);
	}
}
</style>
